import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { NestedTablePrintProps } from "models/propTypes/simpleTableProps";
import { TableEnums } from "models/enums";
//#endregion
//#region components
import { Document, Page, View, Text } from "@react-pdf/renderer";
import NestedTablePrintRow from "./nestedTablePrintRow";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import nestedTableStyles from "theme/custom/nestedTablePdf";
import useTableHelper from "../useTableHelper";

const NestedTablePrint = <RowType extends Record<string, any>, T extends Record<string, any>>(
  props: React.PropsWithChildren<NestedTablePrintProps<RowType, T>>
) => {
  const { properties, repeatProperties, childrenKey, hasChildren, rowDisplayType, rows } = props;

  const columns = Object.entries(properties).filter(([key, value]) => key !== "group");

  const { getNestedTableColumnTitle } = useTableHelper();

  const nonTitleRows = rowDisplayType
    ? props.rows.filter((r) => rowDisplayType(r) !== TableEnums.RowDisplayType.Title)
    : rows;

  const repeatColumnSectionLength =
    repeatProperties && nonTitleRows.length ? nonTitleRows[0][repeatProperties.key]?.length : 0;
  const repeatKeys = repeatProperties?.properties ? Object.keys(repeatProperties.properties).length : 0;
  const maxRepeatColumns = props.maxRepeatColumns ?? (repeatProperties?.showTotal ? 7 : 8);
  const maxRepeatColumnSections = repeatKeys > 0 ? Math.ceil(maxRepeatColumns / repeatKeys) : 0;
  const displayedRepeatColumnLength =
    repeatColumnSectionLength > maxRepeatColumnSections ? maxRepeatColumnSections : repeatColumnSectionLength;

  const repeatColumnDefaultWidth = props.repeatColumnDefaultWidth ?? 75;
  const pdfMaxWidth = 780;
  const nonRepeatColumnWidth = pdfMaxWidth - repeatColumnDefaultWidth * displayedRepeatColumnLength * repeatKeys;

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={nestedTableStyles.page}>
        <View>
          <Text style={nestedTableStyles.printDate}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PrintDate, undefined, {
              date: LocalizationHelperInstance.getFormattedDate(new Date()),
            })}
          </Text>
        </View>
        <View style={nestedTableStyles.titleContainer}>
          <Text style={nestedTableStyles.title}>{props.title}</Text>
          <Text style={nestedTableStyles.subTitle}>{props.subTitle}</Text>
        </View>
        <View style={nestedTableStyles.table}>
          <View fixed style={[nestedTableStyles.row, nestedTableStyles.header]}>
            {columns.map(([headerKey, value], index) => (
              <View
                key={headerKey}
                style={{
                  width: nonRepeatColumnWidth / columns.length,
                  textAlign:
                    value?.alignment === undefined || value?.alignment === "inherit" ? "left" : value?.alignment,
                }}
              >
                <Text style={nestedTableStyles.headerCell}>
                  {value?.description && getNestedTableColumnTitle(value.description)}
                </Text>
              </View>
            ))}

            {[...Array(displayedRepeatColumnLength)].map((col, repeatIndex) => (
              <View key={repeatIndex} style={{ width: repeatColumnDefaultWidth * repeatKeys }}>
                {repeatProperties?.filterField ? (
                  <View>
                    <Text style={[nestedTableStyles.headerCell, { textAlign: "center" }]}>
                      {repeatProperties?.filterField(repeatIndex)}
                    </Text>
                  </View>
                ) : (
                  <></>
                )}
                <View style={nestedTableStyles.flexRow}>
                  {repeatProperties?.properties ? (
                    Object.entries(repeatProperties.properties).map(([repeatKey, value], cellIndex) => (
                      <Text
                        key={repeatKey}
                        style={[
                          repeatProperties?.filterField
                            ? nestedTableStyles.headerSubCell
                            : nestedTableStyles.headerCell,
                          {
                            width: repeatColumnDefaultWidth,
                            textAlign:
                              value?.alignment === undefined || value?.alignment === "inherit"
                                ? "left"
                                : value?.alignment,
                            fontSize: displayedRepeatColumnLength > 8 ? 9 : undefined,
                          },
                        ]}
                      >
                        {value?.description && getNestedTableColumnTitle(value.description, repeatIndex)}
                      </Text>
                    ))
                  ) : (
                    <View></View>
                  )}
                </View>
              </View>
            ))}
            {repeatProperties?.showTotal ? (
              <View style={{ width: repeatColumnDefaultWidth, textAlign: "right" }}>
                <Text
                  style={[nestedTableStyles.headerCell, { fontSize: displayedRepeatColumnLength > 8 ? 9 : undefined }]}
                >{`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InTotal)}`}</Text>
              </View>
            ) : (
              <></>
            )}
          </View>
          {rows.map((row, index, array) => (
            <NestedTablePrintRow
              key={`tab-row-${0}-${index}`}
              row={row}
              index={index}
              path={[]}
              depth={0}
              childrenKey={typeof childrenKey === "function" ? "children" : (childrenKey as string)}
              hasChildren={hasChildren}
              columns={columns}
              repeatProperties={repeatProperties}
              rowDisplayType={props.rowDisplayType}
              repeatColumnsLength={displayedRepeatColumnLength}
              nonRepeatColumnWidth={nonRepeatColumnWidth}
              repeatColumnDefaultWidth={repeatColumnDefaultWidth}
            />
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default NestedTablePrint;
